import React, { useState, useEffect, useCallback, useRef } from 'react'
import Header from '../../components/layout/Header/Header'
import Footer from '../../components/layout/Footer/Footer'
import Pagination from '../../components/DataDisplay/Pagination/Pagination'
import ToastContainer, { useToast } from '../../components/feedback/Toast/Toast'
import MaterialForm from './MaterialForm'
import logoMateriais from '../../assets/images/logo-materiais-didaticos.png'
import { MdWavingHand } from "react-icons/md";
import { IoMdInformationCircle } from 'react-icons/io'
import { HiDownload } from "react-icons/hi";
import { FaRegTrashAlt,  } from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import { BsArrowCounterclockwise } from "react-icons/bs";
import { IoBookOutline } from "react-icons/io5";

import { Material, NivelEnsino, Serie, TipoMaterial } from '../../domain/entities/MaterialForm'
import { SortOptions } from '../../domain/enuns/SortOptions'
import { useRecoilValue } from 'recoil'
import UserState from '../../store/UserAtom'

import * as S from './style'
import { Table } from '@mui/material'

const UrlBase = process.env.REACT_APP_API;

const SORT_OPTIONS = [
  { value: SortOptions.RECENTES, label: 'Mais recentes' },
  { value: SortOptions.TIPO, label: 'Tipo de material' },]

const Materials: React.FC<any> = () => {
  const user = useRecoilValue(UserState)
  const isAdmin = (user?.perfis?.some((p: any) => p.id === '1' || p.nome === 'Administrador'))
  const [view, setView] = useState<'list' | 'form'>('list')
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null)

  const [materiais, setMateriais] = useState<Material[]>([])
  const [totalItems, setTotalItems] = useState(0)
  const [niveisEnsino, setNiveisEnsino] = useState<NivelEnsino[]>([])
  const [series, setSeries] = useState<Serie[]>([])
  const [tipos, setTipos] = useState<TipoMaterial[]>([])
  const [anos, setAnos] = useState<number[]>([])

  const [filterAno, setFilterAno] = useState<string>('')
  const [anoSelected, setAnoSelected] = useState(false)
  const [filterNivel, setFilterNivel] = useState<string>('')
  const [filterSerie, setFilterSerie] = useState<string>('')
  const [filterTipos, setFilterTipos] = useState<number[]>([])
  const [filterTitulo, setFilterTitulo] = useState<string>('')

  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(10)
  
  const [ordenar, setOrdenar] = useState<SortOptions>(SortOptions.RECENTES)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const { toasts, addToast, removeToast } = useToast()

  // Busca os materiais considerando os filtros
  const fetchMateriais = useCallback(async (filtros?: { 
    ano: string | null
    nivel?: string
    serie?: string
    tipos?: number[]
    titulo?: string
  }) => {
    setLoading(true)
    try {
      const ano = filtros?.ano !== undefined ? filtros.ano : null
      const nivel = filtros?.nivel ?? filterNivel
      const serie = filtros?.serie ?? filterSerie
      const tipos = filtros?.tipos ?? filterTipos
      const titulo = filtros?.titulo ?? filterTitulo

      const params = new URLSearchParams()
      if (ano) {
        params.append('ano', ano)
      }
      if (nivel) {
        params.append('nivelEnsino', nivel)
      }
      if (serie) {
        params.append('serie', serie)
      }
      tipos.forEach(tipo => {
        params.append('tipoMaterial', String(tipo))
      })
      if (titulo) {
        params.append('titulo', titulo)
      }

      const endpoint = ano || nivel || serie || tipos.length || titulo ? 'Portal/materiaisDidaticosFiltrados' : 'Portal/materiaisDidaticos'

      const result = await fetch(`${UrlBase}/${endpoint}?${params.toString()}`)
      const data: Material[] = await result.json()

      setMateriais(data)
      setTotalItems(data.length)
      setCurrentPage(1)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [filterAno, filterNivel, filterSerie, filterTipos, filterTitulo])

  const fetchNiveisEnsino = async () => {
    const result = await fetch(`${UrlBase}/Portal/niveisEnsino`)
    const data = await result.json()
    setNiveisEnsino(data)
  }

  const fetchSeries = async (nivelId: number) => {
    const result = await fetch(`${UrlBase}/Portal/series?nivelEnsino=${nivelId}`)
    const data = await result.json()
    setSeries(data)
  }

  const fetchTipos = async () => {
    const result = await fetch(`${UrlBase}/Portal/tipos`)
    const data = await result.json()
    setTipos(data)
  }

  const fetchAnos = async () => {
    const result = await fetch(`${UrlBase}/Portal/anos`)
    const data = await result.json()
    setAnos(data)

    // Verifica se existe algum ano e define o filtro
    const anoRecente = data.length > 0 ? String(data[0]) : null
    if (anoRecente) {
      setFilterAno(anoRecente)
      setAnoSelected(false)
    } else {
      setFilterAno('')
      setAnoSelected(true)
    }

    // Já carrega os materiais filtrados desde o inicio
    fetchMateriais({ ano: anoRecente })
  }

  useEffect(() => {
    fetchNiveisEnsino()
    fetchTipos()
    fetchAnos()
  }, [])

  // Organizar a listagem de materiais de acordo com a opção de ordenação
  const materiaisOrdenados = [...materiais].sort((a, b) => {
    if (ordenar === SortOptions.RECENTES) {
      return new Date(b.ultimaAlteracao).getTime() - new Date(a.ultimaAlteracao).getTime()
    }
    if (ordenar === SortOptions.TIPO) {
      if (a.tipoId === null) {
        a.tipoId = tipos.length + 1;
      }
      else if (b.tipoId === null) {
        b.tipoId = tipos.length + 1;
      }
      return (a.tipoId ?? 0) - (b.tipoId ?? 0)
    }
    return 0
  })

  const materiaisPaginados = materiaisOrdenados.slice(
    (currentPage - 1) * itemsPorPagina,
    currentPage * itemsPorPagina
  )

  const totalPages = Math.ceil(totalItems / itemsPorPagina)

  const handleClearFilters = () => {
    const anoRecente = anos.length > 0 ? String(anos[0]) : ''

    setFilterAno(anoRecente)
    setAnoSelected(false)
    setFilterNivel('')
    setFilterSerie('')
    setFilterTipos([])
    setFilterTitulo('')
    setSeries([])
    fetchMateriais({ ano: anoRecente || null, nivel: '', serie: '', tipos: [], titulo: '' })
  }

  const handleNivelChange = (nivelId: string) => {
    setFilterNivel(nivelId)
    setFilterSerie('')
    if (nivelId) {
      fetchSeries(Number(nivelId))
    }
    else 
      setSeries([])
  }

  const handleTipoToggle = (id: number) => {
    setFilterTipos(prev =>
      prev.includes(id) ? prev.filter(tipoId => tipoId !== id) : [...prev, id]
    )
  }

  const handleAnoChange = (value: string) => {
    if (value === '') {
      setFilterAno('')
      setAnoSelected(true)
      return
    }

    setFilterAno(value)
    setAnoSelected(false)
  }

  const currentAnoFilter = anoSelected ? null : (filterAno || null)

  const handleEdit = (material: Material) => {
    setEditingMaterial(material)
    setView('form')
    setOpenMenuId(null)
  }

  const handleCreate = () => {
    setEditingMaterial(null)
    setView('form')
  }

  const handleCancel = () => {
    setEditingMaterial(null)
    setView('list')
  }

  // Salvar os dados do formulário a refazer a listagem
  const handleFormSave = (isEditing: boolean) => {
    const anoRecente = anos.length > 0 ? String(anos[0]) : ''
    setEditingMaterial(null)
    setView('list')
    fetchMateriais({ ano: anoRecente })
    addToast(
      isEditing ? 'Material didático atualizado com sucesso.' : 'Material didático cadastrado com sucesso.',
    )
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deseja realmente excluir este material didático?')) 
      return ;

    try {
      await fetch(`${UrlBase}/Portal/materiaisDidaticos/material?id=${id}`, 
        { 
          method: 'DELETE' 
        })
      fetchMateriais({ ano: currentAnoFilter })
      addToast('Material didático excluído com sucesso.')
    } catch (error) {
      console.error(error)
      addToast('Não foi possível excluir o material. Tente novamente mais tarde.')
    }
    setOpenMenuId(null)
  }

  // Baixar o arquivo do material
  const handleDownload = async (nomeArquivo: string, titulo: string) => {
    try {
      const url = `${UrlBase}/Portal/materiaisDidaticos/download/${encodeURIComponent(nomeArquivo)}`
      const res = await fetch(url)

      if (!res.ok) {
        throw new Error()
      }

      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = objectUrl
      link.download = titulo
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
      addToast('Download iniciado. O material será salvo no seu dispositivo em instantes.')
    } catch {
      addToast('Não foi possível baixar o material no momento. Tente novamente mais tarde.')
    }
  }

  // Abrir página do formulário de criação/edição de material
  if (view === 'form') {
    return (
      <>
        <Header />
        <MaterialForm
          material={editingMaterial}
          niveisEnsino={niveisEnsino}
          tipos={tipos.slice(0, -1)}
          anos={anos}
          onCancel={handleCancel}
          onSave={handleFormSave}
          onDownload={handleDownload}
          onError={(message) => addToast(message)}
        />
        <ToastContainer toasts={toasts} onClose={removeToast} />
        <Footer />
      </>
    )
  }

  return (
    <>
      <Header />

      <S.PageWrapper>
        <S.TopBar>
          <img src={logoMateriais}/>
          <S.NewMaterialButton onClick={handleCreate} style={{ display: isAdmin ? 'flex' : 'none' }}>
            <IoMdAdd /> Novo Material
          </S.NewMaterialButton>
        </S.TopBar>

        <S.BannerRow>
          <S.BannerWelcome>
            <MdWavingHand size={32} style={{ marginRight: '10px' }} />
            <div>
              <strong>Bem-vindo(a)!</strong>
              <p>
                Aqui você encontra os materiais didáticos disponibilizados pela SEDUC Goiás para
                apoiar o planejamento e o desenvolvimento das suas aulas.
              </p>
            </div>
          </S.BannerWelcome>
          <S.BannerInfo>
            <IoMdInformationCircle size={65} color="#007bff" />
            <p>
              Em caso de dúvidas sobre os materiais, entre em contato com a equipe pedagógica da
              sua regional.
            </p>
          </S.BannerInfo>
        </S.BannerRow>

        <S.ListagemContentArea>
          <S.Sidebar>
            <S.SidebarHeader>
              <S.SidebarTitle>Filtros</S.SidebarTitle>
              <S.ClearFiltersBtn onClick={handleClearFilters}>
                Limpar filtros <BsArrowCounterclockwise />
              </S.ClearFiltersBtn>
            </S.SidebarHeader>

            <S.FilterGroup>
              <label>Ano</label>
              <S.Select value={filterAno} onChange={e => handleAnoChange(e.target.value)}>
                <option value="">Selecione</option>
                {anos.map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </S.Select>
            </S.FilterGroup>
            <S.FilterGroup>
              <label>Nível de Ensino</label>
              <S.Select value={filterNivel} onChange={e => handleNivelChange(e.target.value)}>
                <option value="">Selecione</option>
                {niveisEnsino.map(n => (
                  <option key={n.id} value={n.id}>{n.descricao}</option>
                ))}
              </S.Select>
            </S.FilterGroup>
            <S.FilterGroup>
              <label>Série</label>
              <S.Select
                value={filterSerie}
                onChange={e => setFilterSerie(e.target.value)}
                disabled={!filterNivel}
              >
                <option value="">Selecione</option>
                {series.map(s => (
                  <option key={s.id} value={s.id}>{s.descricao}</option>
                ))}
              </S.Select>
            </S.FilterGroup>
            <S.FilterGroup>
              <label>Tipo de Material</label>
              {tipos.map(t => (
                <S.CheckboxLabel key={t.id}>
                  <input
                    type="checkbox"
                    checked={filterTipos.includes(t.id)}
                    onChange={() => handleTipoToggle(t.id)}
                  />
                  {t.titulo}
                </S.CheckboxLabel>
              ))}
            </S.FilterGroup>
            <S.FilterGroup>
              <label>Nome do material</label>
              <S.Input
                placeholder="Digite o nome do material"
                maxLength={200}
                value={filterTitulo}
                onChange={e => setFilterTitulo(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchMateriais()}
              />
            </S.FilterGroup>

            <S.ApplyFiltersBtn onClick={() => fetchMateriais({ ano: currentAnoFilter, nivel: filterNivel, serie: filterSerie, tipos: filterTipos, titulo: filterTitulo })}>
              Aplicar filtros
            </S.ApplyFiltersBtn>
          </S.Sidebar>

          <S.TableArea>
            <S.TableHeader>
              <S.TableTitle>Materiais cadastrados ({totalItems})</S.TableTitle>
              <S.SortRow>
                <span>Ordenar por:</span>
                <S.Select
                  value={ordenar}
                  onChange={e => setOrdenar(e.target.value as SortOptions)}
                  style={{ width: 'auto' }}
                >
                  {SORT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </S.Select>
              </S.SortRow>
            </S.TableHeader>

            
            {loading ? (<S.LoadingText>Carregando...</S.LoadingText>) : (
              <Table>
                <thead>
                  <tr>
                    <S.Th>Nome do documento</S.Th>
                    <S.Th>Ano</S.Th>
                    <S.Th>Nível de Ensino</S.Th>
                    <S.Th>Série</S.Th>
                    <S.Th>Tipo de Material</S.Th>
                    <S.Th>Atualizado em</S.Th>
                    <S.Th>Ações</S.Th>
                  </tr>
                </thead>
                <tbody>
                  {materiaisPaginados.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <S.EmptyState>Nenhum material encontrado.</S.EmptyState>
                      </td>
                    </tr>
                  ) : (
                    materiaisPaginados.map(m => (
                      <tr key={m.id}>
                        <S.Td>
                          <S.DocNameCell>
                            <S.IconBackground style={{ backgroundColor: m.cor }}>
                              <IoBookOutline  size={25} color='white' />
                            </S.IconBackground>
                            <span>{m.tituloMaterial}</span>
                          </S.DocNameCell>
                        </S.Td>
                        <S.Td>{m.ano}</S.Td>
                        <S.Td>{m.descricaoNivelEnsino || 'Todos os níveis'}</S.Td>
                        <S.Td>{m.descricaoSerie || '-'}</S.Td>
                        <S.Td>
                          {m.descricaoTipo ? (
                            <S.TipoBadge $tipo={m.tipoId ?? undefined}>{m.descricaoTipo || '-'}</S.TipoBadge>
                          ) : '-'}
                        </S.Td>
                        <S.Td>{m.ultimaAlteracao ? new Date(m.ultimaAlteracao).toLocaleString('pt-BR') : '-'}</S.Td>
                        <S.Td>
                          <S.ActionsCell>
                            <S.DownloadBtn onClick={() => handleDownload(m.anexo, m.tituloMaterial)}>
                              <HiDownload  color="#16a34a"/> Baixar arquivo
                            </S.DownloadBtn>
                            <div style={{ display: isAdmin ? 'flex' : 'none', position: 'relative'}}>
                              <S.MenuBtn
                                onClick={() =>
                                  setOpenMenuId(openMenuId === m.id ? null : m.id)
                                }
                              > ⋮
                              </S.MenuBtn>{openMenuId === m.id && (
                                <S.DropdownMenu>
                                  <S.DropdownItem onClick={() => handleEdit(m)}>
                                    <GoPencil /> Editar
                                  </S.DropdownItem>
                                  <S.DropdownItem
                                    $danger
                                    onClick={() => handleDelete(m.id)}
                                  >
                                    <FaRegTrashAlt /> Excluir
                                  </S.DropdownItem>
                                </S.DropdownMenu>
                              )}
                            </div>
                          </S.ActionsCell>
                        </S.Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>)}
            
              <S.PaginationRow>
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  setPage={setCurrentPage}
                />
                <S.SortRow>
                  <span>Itens por página:</span>
                  <S.Select
                    value={itemsPorPagina}
                    onChange={e => {
                      setItemsPorPagina(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                    style={{ width: 'auto' }}
                  >
                    {[10, 50, 100].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </S.Select>
                </S.SortRow>
              </S.PaginationRow>
            
          </S.TableArea>
        </S.ListagemContentArea>
      </S.PageWrapper>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <Footer />
    </>
  )
}

export default Materials
